<script setup lang="ts">
import {
  buildEnvUrl,
  type EnvironmentColor,
  type EnvironmentDescriptor,
} from '../utils/environment'

const { compact = false } = defineProps<{
  /** Render the trigger as a colored dot only (for the collapsed sidebar). */
  compact?: boolean
}>()

const route = useRoute()
const { current, environments } = useEnvironment()

const DOT_CLASS: Record<EnvironmentColor, string> = {
  error: 'bg-error-500',
  info: 'bg-info-500',
  warning: 'bg-warning-500',
  success: 'bg-success-500',
  neutral: 'bg-neutral-500',
}

function switchTo(env: EnvironmentDescriptor) {
  if (env.key === current.key) return
  return navigateTo(buildEnvUrl(env, route.fullPath), { external: true })
}

const items = environments.map((env) => ({
  label: env.label,
  slot: 'env' as const,
  env,
  checked: env.key === current.key,
  onSelect: () => switchTo(env),
}))
</script>

<template>
  <UDropdownMenu
    :content="{ align: 'start' }"
    :items="items"
    :ui="{ content: 'w-72' }"
  >
    <UButton
      class="z-10 rounded-full"
      :aria-label="`Environment: ${current.label}`"
      :square="compact"
      color="neutral"
      size="sm"
      variant="subtle"
    >
      <span
        :class="['size-2 shrink-0 rounded-full', DOT_CLASS[current.color]]"
      />
      <template v-if="!compact">
        <span class="font-medium">{{ current.label }}</span>
        <UIcon name="i-ph-caret-down" class="size-3.5 opacity-60" />
      </template>
    </UButton>

    <template #env="{ item }">
      <div class="flex w-full flex-col gap-0.5 py-1">
        <div class="flex items-center gap-2">
          <span
            :class="[
              'size-2.5 shrink-0 rounded-full',
              DOT_CLASS[item.env.color],
            ]"
          />
          <span class="text-highlighted font-medium">
            {{ item.env.label }}
          </span>
          <UBadge :color="item.env.color" size="sm" variant="subtle">
            {{ item.env.shortLabel }}
          </UBadge>
          <UIcon
            v-if="item.checked"
            name="i-ph-check"
            class="text-primary ml-auto size-4 shrink-0"
          />
        </div>
        <div class="flex items-center gap-2">
          <span class="size-2.5 shrink-0" aria-hidden="true" />
          <span class="text-muted text-xs">{{ item.env.description }}</span>
        </div>
      </div>
    </template>
  </UDropdownMenu>
</template>
